import ContainerWrapper from "@/components/ui/ContainerWrapper";
import { useUsersControllerDeleteUser } from "@/lib/lutely-api/gen/users/users";
import { Button } from "@/stories/Button";
import * as Dialog from "@radix-ui/react-dialog";
import {
  Button as RadixButton,
  Flex,
  Text,
  TextField,
  Theme,
} from "@radix-ui/themes";
import { createFileRoute } from "@tanstack/react-router";
import { isAxiosError } from "axios";
import { useState } from "react";
import { toast } from "react-toastify";

function DeleteUserPage() {
  const [email, setEmail] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const deleteUserMutation = useUsersControllerDeleteUser();

  const trimmedEmail = email.trim();

  const handleRequestDelete = (e: React.FormEvent) => {
    e.preventDefault();
    if (!trimmedEmail) {
      toast.error("Please enter an email address");
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteUserMutation.mutateAsync({
        params: { email: trimmedEmail },
      });
      toast.success(`User ${trimmedEmail} deleted`);
      setConfirmOpen(false);
      setEmail("");
    } catch (error) {
      const message = isAxiosError(error)
        ? (error.response?.data?.message ?? error.message)
        : "Failed to delete user";
      toast.error(message);
    }
  };

  const isPending = deleteUserMutation.isPending;

  return (
    <ContainerWrapper className="py-8">
      <Flex direction="column" gap="6" style={{ maxWidth: 500 }}>
        <Flex direction="column" gap="2">
          <Text size="8" weight="bold">
            Delete User
          </Text>
          <Text size="4" color="gray">
            Permanently delete a user account by email address. This action
            cannot be undone.
          </Text>
        </Flex>

        <form onSubmit={handleRequestDelete}>
          <Flex direction="column" gap="4">
            <Flex direction="column" gap="2">
              <label htmlFor="email">
                <Text size="2" weight="medium">
                  Email Address
                </Text>
              </label>
              <TextField.Root
                id="email"
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isPending}
              />
            </Flex>

            <Flex gap="3">
              <Button
                type="submit"
                label="Delete User"
                color="red"
                disabled={isPending || !trimmedEmail}
              />
            </Flex>
          </Flex>
        </form>
      </Flex>

      <Dialog.Root open={confirmOpen} onOpenChange={setConfirmOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-[456px] bg-[#fafbfd] rounded-[8px] shadow-[0px_14px_20px_-1px_rgba(5,12,43,0.14)] z-50">
            <Theme>
              <Flex
                direction="column"
                className="pt-[24px] pb-[16px] gap-[24px]"
              >
                <Flex direction="column" className="px-[32px] gap-[8px]">
                  <Text className="font-open-sans font-semibold text-[18px] leading-[1.4] text-black">
                    Delete this user?
                  </Text>
                  <Text className="font-open-sans font-normal text-[14px] leading-[1.4] text-[#515459]">
                    <strong>{trimmedEmail}</strong> will be permanently deleted.
                    This action cannot be undone.
                  </Text>
                </Flex>

                <Flex className="px-[24px] gap-[4px] justify-end">
                  <RadixButton
                    onClick={() => setConfirmOpen(false)}
                    disabled={isPending}
                    color="gray"
                    size="3"
                    className="h-[40px] rounded-full"
                  >
                    Cancel
                  </RadixButton>
                  <RadixButton
                    onClick={handleConfirmDelete}
                    disabled={isPending}
                    color="red"
                    size="3"
                    className="flex-1 h-[40px]"
                    radius="full"
                  >
                    {isPending ? "Deleting..." : "Delete User"}
                  </RadixButton>
                </Flex>
              </Flex>
            </Theme>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </ContainerWrapper>
  );
}

export const Route = createFileRoute("/admin/_adminLayout/delete-user")({
  component: DeleteUserPage,
});
